import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { providersAPI, reviewsAPI } from '../lib/api';
import { extUrl } from '../lib/url';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Search, Star, MapPin, CheckCircle, Calendar, Phone, Mail,
  ExternalLink, Loader2, Briefcase, ChevronLeft, ChevronRight,
  Heart, Globe, MessageCircle, Plus, CalendarDays, Crown, ArrowRight, Check,
  Eye, BarChart3, Sparkles
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import LocationAutocomplete from '../components/LocationAutocomplete';
import usePageTitle from '../hooks/usePageTitle';

const DEFAULT_CATEGORIES = [
  { value: 'career_coach', label: 'Career Coach' },
  { value: 'real_estate', label: 'Realtor' },
  { value: 'financial', label: 'Financial Advisor' },
  { value: 'legal', label: 'Attorney' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'therapist', label: 'Therapist' },
  { value: 'travel', label: 'Travel Agent' },
  { value: 'career', label: 'Career Services' },
  { value: 'tech', label: 'Tech Consultant' },
  { value: 'tutor', label: 'Tutor' },
  { value: 'photographer', label: 'Photographer' },
  { value: 'event_planner', label: 'Event Planner' },
  { value: 'personal_trainer', label: 'Personal Trainer' },
  { value: 'graphic_designer', label: 'Graphic Designer' },
  { value: 'web_developer', label: 'Web Developer' },
  { value: 'hair_stylist', label: 'Hair Stylist' },
  { value: 'makeup_artist', label: 'Makeup Artist' },
  { value: 'caterer', label: 'Caterer' },
  { value: 'dj', label: 'DJ' },
  { value: 'music_instructor', label: 'Music Instructor' },
];

const POPULAR_SERVICES = [
  'Career Coach', 'Realtor', 'Financial Advisor', 'Attorney',
  'Therapist', 'Tax Preparer', 'Insurance Agent', 'Resume Writer',
];

const StarRating = ({ rating, size = 'sm', interactive = false, onChange }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center">
      {stars.map(s => (
        <button key={s} onClick={() => interactive && onChange?.(s)} className={interactive ? 'cursor-pointer' : 'cursor-default'}
          type="button">
          <Star className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} ${s <= rating ? 'text-amber-400 fill-current' : 'text-slate-200'}`} />
        </button>
      ))}
    </div>
  );
};

const ServiceDirectory = () => {
  usePageTitle('Services');
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [selected, setSelected] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [page, setPage] = useState(1);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const LIMIT = 12;

  // Premium state
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [showPremiumPlan, setShowPremiumPlan] = useState(false);

  // Build merged category list: defaults + any custom categories from DB, deduplicated
  const allCategories = (() => {
    const defaultValues = new Set(DEFAULT_CATEGORIES.map(c => c.value));
    const extras = dynamicCategories
      .filter(val => val && !defaultValues.has(val))
      .map(val => ({ value: val, label: val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }));
    return [{ value: 'all', label: 'All Categories' }, ...DEFAULT_CATEGORIES, ...extras];
  })();

  // Self-registration
  const [showRegister, setShowRegister] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [newProvider, setNewProvider] = useState({
    category: 'career_coach', custom_category: '', business_name: '', business_description: '',
    specialties: '', location: '', contact_email: '', contact_phone: '',
    website_url: '', calendly_link: '', pricing: '',
  });

  useEffect(() => { fetchProviders(); }, [category, page]);

  // Fetch dynamic categories from DB on mount
  useEffect(() => {
    providersAPI.getCategories()
      .then(res => setDynamicCategories(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // Fetch premium status
  useEffect(() => {
    providersAPI.premiumStatus().then(r => setPremiumStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const urlCategory = searchParams.get('category');
    if (urlCategory && urlCategory !== category) setCategory(urlCategory);
  }, [searchParams]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: (page - 1) * LIMIT };
      if (category !== 'all') params.category = category;
      if (search) params.search = search;
      if (locationSearch) params.location = locationSearch;
      const res = await providersAPI.list(params);
      setProviders(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProvider = async (provider) => {
    setSelected(provider);
    setLoadingReviews(true);
    try {
      const res = await reviewsAPI.list(provider.provider_id);
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (e) { setReviews([]); }
    finally { setLoadingReviews(false); }
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) { toast.error('Please write a review'); return; }
    setSubmittingReview(true);
    try {
      await reviewsAPI.create({ provider_id: selected.provider_id, rating: newReview.rating, comment: newReview.comment });
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setNewReview({ rating: 5, comment: '' });
      const res = await reviewsAPI.list(selected.provider_id);
      setReviews(Array.isArray(res.data) ? res.data : []);
    } catch (error) { toast.error('Failed to submit review'); }
    finally { setSubmittingReview(false); }
  };

  const handleRegister = async () => {
    if (!newProvider.business_name || !newProvider.business_description) {
      toast.error('Business name and description are required');
      return;
    }
    if (newProvider.category === 'other' && !newProvider.custom_category.trim()) {
      toast.error('Please enter your custom category');
      return;
    }
    setRegistering(true);
    try {
      const payload = {
        ...newProvider,
        category: newProvider.category === 'other' ? newProvider.custom_category.trim().toLowerCase().replace(/\s+/g, '_') : newProvider.category,
        specialties: newProvider.specialties ? newProvider.specialties.split(',').map(s => s.trim()).filter(Boolean) : [],
        name: user?.name,
      };
      delete payload.custom_category;
      await providersAPI.create(payload);
      toast.success('Provider profile created! Pending verification.');
      setShowRegister(false);
      setNewProvider({ category: 'career_coach', custom_category: '', business_name: '', business_description: '', specialties: '', location: '', contact_email: '', contact_phone: '', website_url: '', calendly_link: '', pricing: '' });
      fetchProviders();
      // Refresh dynamic categories in case a custom one was added
      providersAPI.getCategories()
        .then(res => setDynamicCategories(Array.isArray(res.data) ? res.data : []))
        .catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    } finally {
      setRegistering(false);
    }
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Service Providers</h1>
            <p className="text-slate-gray mt-1">Find trusted alumni professionals across all categories</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowRegister(true)} className="bg-dark-slate text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" />Become a Provider
            </Button>
          </div>
        </div>

        {/* Premium upsell / status banner */}
        {premiumStatus?.has_provider_profile && !premiumStatus?.is_active && (
          <div className="rounded-2xl p-4 mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-sm font-medium text-dark-slate">Upgrade to Premium Provider</p>
                <p className="text-xs text-slate-gray">Get featured placement, profile analytics, and a premium badge — $19/mo</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowPremiumPlan(true)} className="rounded-full bg-amber-500 text-white hover:bg-amber-600 text-xs">
              <Crown className="w-3.5 h-3.5 mr-1" />Upgrade
            </Button>
          </div>
        )}
        {premiumStatus?.is_active && (
          <div className="rounded-2xl p-3 mb-4 bg-gradient-to-r from-amber-50 to-orange-50/30 border border-amber-200/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-lg">⭐</span>
              <p className="text-sm font-medium text-dark-slate">
                Premium Provider
                {premiumStatus.premium_status === 'trial' && <span className="text-xs text-amber-600 ml-2">(Trial)</span>}
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-gray">
              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{premiumStatus.profile_views} views</span>
            </div>
          </div>
        )}

        {/* Premium Plan Dialog */}
        <Dialog open={showPremiumPlan} onOpenChange={setShowPremiumPlan}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate">Premium Provider</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-gray mb-4">Stand out from the crowd. Premium providers get top placement and powerful tools to grow their practice.</p>
            <div className="rounded-2xl p-5 border-2 border-amber-300 bg-amber-50/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">⭐</span>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-dark-slate">Premium</h3>
                    <p className="text-xs text-slate-gray">14-day free trial</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-2xl text-dark-slate">$19</p>
                  <p className="text-xs text-slate-gray">/month</p>
                </div>
              </div>
              <ul className="space-y-1.5 mb-4">
                {[
                  'Premium badge & priority placement',
                  'Boosted to top of search results',
                  'Profile view analytics',
                  'Video introduction on your profile',
                  'Portfolio gallery showcase',
                  'Homepage spotlight rotation',
                ].map(perk => (
                  <li key={perk} className="flex items-center gap-2 text-sm text-slate-gray">
                    <Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{perk}
                  </li>
                ))}
              </ul>
              {premiumStatus?.is_active ? (
                <Button disabled className="w-full rounded-full bg-slate-100 text-slate-500">Current Plan</Button>
              ) : (
                <Button
                  onClick={async () => {
                    try {
                      const res = await providersAPI.premiumSubscribe({ origin_url: window.location.origin });
                      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                    } catch (e) { toast.error(e.response?.data?.detail || 'Subscription failed'); }
                  }}
                  className="w-full rounded-full bg-amber-500 text-white hover:bg-amber-600"
                >
                  Start Free Trial <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
            <Input placeholder="Search providers..." value={search} onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchProviders()} className="pl-10 h-11 rounded-xl bg-white border-slate-200" />
          </div>
          <div className="flex-1 sm:max-w-[200px]">
            <LocationAutocomplete
              value={locationSearch}
              onChange={setLocationSearch}
              onSelect={(loc) => { setLocationSearch(loc); setTimeout(fetchProviders, 100); }}
              placeholder="City or zip..."
              className="h-11 rounded-xl bg-white border-slate-200"
            />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="h-11 w-[180px] rounded-xl bg-white border-slate-200">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              {allCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={fetchProviders} className="h-11 bg-dark-slate text-white rounded-xl px-6">
            <Search className="w-4 h-4 mr-2" />Search
          </Button>
        </div>

        {/* Popular Service Chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {POPULAR_SERVICES.map(svc => (
            <button key={svc} onClick={() => { setSearch(svc); fetchProviders(); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium bg-white text-slate-gray border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors">
              {svc}
            </button>
          ))}
        </div>

        {/* Premium callout for non-providers */}
        {!premiumStatus?.has_provider_profile && !premiumStatus?.is_active && (
          <div className="rounded-2xl mb-6 overflow-hidden border border-amber-200/60 bg-gradient-to-r from-amber-50 via-white to-orange-50">
            <div className="flex flex-col sm:flex-row items-center gap-4 p-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-amber-500" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-heading font-bold text-dark-slate">Are you a professional? List your services here.</h3>
                <p className="text-sm text-slate-gray mt-0.5">
                  Join for free, or go <span className="font-semibold text-amber-600">Premium</span> for $19/mo to get featured placement, a premium badge, profile analytics, and more.
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button variant="outline" onClick={() => setShowRegister(true)} className="rounded-full text-sm">
                  List Free
                </Button>
                <Button onClick={() => setShowPremiumPlan(true)} className="rounded-full bg-amber-500 text-white hover:bg-amber-600 text-sm">
                  <Crown className="w-3.5 h-3.5 mr-1" />Go Premium
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Provider Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-slate-gray" /></div>
        ) : providers.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <Briefcase className="w-12 h-12 text-slate-gray mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No providers found</h3>
            <p className="text-slate-gray">Try different search terms or category</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {providers.map(provider => (
                <div key={provider.provider_id}
                  className={`bg-white rounded-2xl p-5 border shadow-ambient hover:shadow-hover transition-shadow cursor-pointer ${
                    provider.premium ? 'border-amber-200 ring-1 ring-amber-100' : 'border-slate-100'
                  }`}
                  onClick={() => handleSelectProvider(provider)}>
                  <div className="flex items-start space-x-4 mb-3">
                    <Avatar className="w-14 h-14">
                      <AvatarImage src={provider.profile_photo || provider.photos?.[0]} />
                      <AvatarFallback className="bg-mint-fresh text-dark-slate text-lg">{provider.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-1">
                        <h3 className="font-bold text-dark-slate line-clamp-1">{provider.name}</h3>
                        {provider.verified && <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                        {provider.premium && <span className="text-sm flex-shrink-0" title="Premium Provider">⭐</span>}
                      </div>
                      {provider.business_name && <p className="text-sm text-slate-gray line-clamp-1">{provider.business_name}</p>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); toggleFavorite(provider.provider_id); }}
                      className="p-1.5 rounded-full hover:bg-slate-100">
                      <Heart className={`w-4 h-4 ${favorites.has(provider.provider_id) ? 'text-rose-500 fill-current' : 'text-slate-gray'}`} />
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 mb-2">
                    <Badge variant="secondary" className="bg-slate-100 text-xs">{(provider.category || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                    {provider.premium && <Badge className="text-[10px] bg-amber-100 text-amber-700 border border-amber-200">Premium</Badge>}
                  </div>

                  <div className="flex items-center space-x-2 mb-2">
                    <StarRating rating={provider.average_rating || 0} />
                    <span className="text-xs text-slate-gray">({provider.total_reviews || 0} reviews)</span>
                  </div>

                  {provider.location && (
                    <p className="text-xs text-slate-gray flex items-center mb-2"><MapPin className="w-3 h-3 mr-1" />{provider.location}</p>
                  )}

                  {provider.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {provider.specialties.slice(0, 3).map((s, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{s}</Badge>
                      ))}
                    </div>
                  )}

                  {/* Book Now button */}
                  <Link to={`/bookings?provider=${provider.provider_id}`} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" className="w-full rounded-full bg-dark-slate text-white mt-1">
                      <CalendarDays className="w-3.5 h-3.5 mr-1.5" />Book Now
                    </Button>
                  </Link>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center space-x-3">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-gray font-medium">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={providers.length < LIMIT} className="rounded-full">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}

        {/* Provider Detail Modal */}
        <Dialog open={!!selected} onOpenChange={() => { setSelected(null); setShowReviewForm(false); }}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
            {selected && (
              <>
                <DialogHeader>
                  <div className="flex items-center space-x-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={selected.profile_photo || selected.photos?.[0]} />
                      <AvatarFallback className="bg-mint-fresh text-dark-slate text-2xl">{selected.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center space-x-2">
                        <DialogTitle className="font-heading text-2xl">{selected.name}</DialogTitle>
                        {selected.verified && <CheckCircle className="w-5 h-5 text-emerald-500" />}
                        {selected.premium && <Badge className="text-xs bg-amber-100 text-amber-700 border border-amber-200">⭐ Premium</Badge>}
                      </div>
                      {selected.business_name && <p className="text-slate-gray">{selected.business_name}</p>}
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-5 mt-4">
                  <div className="flex items-center space-x-3">
                    <StarRating rating={selected.average_rating || 0} size="md" />
                    <span className="text-sm text-slate-gray">{selected.average_rating?.toFixed(1) || '0'} ({selected.total_reviews || 0} reviews)</span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">{(selected.category || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                    {selected.location && <Badge variant="secondary" className="bg-slate-100"><MapPin className="w-3 h-3 mr-1" />{selected.location}</Badge>}
                    {selected.years_experience && <Badge variant="secondary" className="bg-blue-50 text-blue-700">{selected.years_experience}+ years exp</Badge>}
                  </div>

                  {selected.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {selected.specialties.map((s, i) => <Badge key={i} variant="outline">{s}</Badge>)}
                    </div>
                  )}

                  {/* Video intro for premium providers */}
                  {selected.premium && selected.video_intro && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Video Introduction</h4>
                      <video src={selected.video_intro} controls className="w-full rounded-xl max-h-64 bg-black" />
                    </div>
                  )}

                  {selected.business_description && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">About</h4>
                      <p className="text-slate-gray text-sm whitespace-pre-wrap">{selected.business_description}</p>
                    </div>
                  )}

                  {/* Portfolio for premium providers */}
                  {selected.premium && selected.portfolio?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Portfolio</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {selected.portfolio.map((img, i) => (
                          <img key={i} src={img} alt={`Portfolio ${i + 1}`} className="w-full aspect-square object-cover rounded-xl" />
                        ))}
                      </div>
                    </div>
                  )}

                  {selected.credentials && (
                    <div>
                      <h4 className="font-semibold text-dark-slate mb-2">Credentials</h4>
                      <p className="text-slate-gray text-sm whitespace-pre-wrap">{selected.credentials}</p>
                    </div>
                  )}

                  {/* Contact buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Link to={`/bookings?provider=${selected.provider_id}`}>
                      <Button className="bg-dark-slate text-white rounded-full"><CalendarDays className="w-4 h-4 mr-2" />Book Now</Button>
                    </Link>
                    {selected.calendly_link && (
                      <a href={extUrl(selected.calendly_link)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="rounded-full"><Calendar className="w-4 h-4 mr-2" />Calendly</Button>
                      </a>
                    )}
                    {selected.contact_email && (
                      <a href={`mailto:${selected.contact_email}`}>
                        <Button variant="outline" className="rounded-full"><Mail className="w-4 h-4 mr-2" />Email</Button>
                      </a>
                    )}
                    {selected.contact_phone && (
                      <a href={`tel:${selected.contact_phone}`}>
                        <Button variant="outline" className="rounded-full"><Phone className="w-4 h-4 mr-2" />Call</Button>
                      </a>
                    )}
                    {selected.website_url && (
                      <a href={extUrl(selected.website_url)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="rounded-full"><Globe className="w-4 h-4 mr-2" />Website</Button>
                      </a>
                    )}
                  </div>

                  {/* Reviews section */}
                  <div className="border-t border-slate-100 pt-5">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-dark-slate">Reviews ({reviews.length})</h4>
                      <Button variant="outline" size="sm" onClick={() => setShowReviewForm(!showReviewForm)} className="rounded-full text-xs">
                        Write Review
                      </Button>
                    </div>

                    {showReviewForm && (
                      <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
                        <div>
                          <Label className="text-sm">Rating</Label>
                          <StarRating rating={newReview.rating} size="md" interactive onChange={(r) => setNewReview(p => ({ ...p, rating: r }))} />
                        </div>
                        <Textarea value={newReview.comment} onChange={(e) => setNewReview(p => ({ ...p, comment: e.target.value }))}
                          placeholder="Share your experience..." rows={3} />
                        <Button onClick={handleSubmitReview} disabled={submittingReview} className="bg-dark-slate text-white rounded-full">
                          {submittingReview ? 'Submitting...' : 'Submit Review'}
                        </Button>
                      </div>
                    )}

                    {loadingReviews ? (
                      <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin text-slate-gray mx-auto" /></div>
                    ) : reviews.length === 0 ? (
                      <p className="text-slate-gray text-sm text-center py-4">No reviews yet. Be the first!</p>
                    ) : (
                      <div className="space-y-3">
                        {reviews.map(review => (
                          <div key={review.review_id} className="bg-slate-50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-dark-slate text-sm">{review.user_name}</span>
                                <StarRating rating={review.rating} />
                              </div>
                              {review.created_at && <span className="text-xs text-slate-gray">{formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}</span>}
                            </div>
                            <p className="text-sm text-slate-gray">{review.comment}</p>
                            {review.provider_response && (
                              <div className="mt-2 pl-3 border-l-2 border-emerald-300">
                                <p className="text-xs font-medium text-emerald-700">Provider Response</p>
                                <p className="text-sm text-slate-gray">{review.provider_response}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Become a Provider Dialog */}
        <Dialog open={showRegister} onOpenChange={setShowRegister}>
          <DialogContent className="max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-dark-slate">Become a Provider</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-gray mb-4">Register as a service provider to be listed in the directory and receive bookings from alumni.</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category *</Label>
                  <Select value={newProvider.category} onValueChange={(v) => setNewProvider(p => ({ ...p, category: v, custom_category: '' }))}>
                    <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {allCategories.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      <SelectItem value="other">Other (Custom)</SelectItem>
                    </SelectContent>
                  </Select>
                  {newProvider.category === 'other' && (
                    <Input value={newProvider.custom_category} onChange={(e) => setNewProvider(p => ({ ...p, custom_category: e.target.value }))}
                      placeholder="Enter your category" className="mt-2 rounded-xl" />
                  )}
                </div>
                <div>
                  <Label>Business Name *</Label>
                  <Input value={newProvider.business_name} onChange={(e) => setNewProvider(p => ({ ...p, business_name: e.target.value }))}
                    placeholder="Your business name" className="mt-1 rounded-xl" />
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea value={newProvider.business_description} onChange={(e) => setNewProvider(p => ({ ...p, business_description: e.target.value }))}
                  placeholder="Describe your services..." rows={3} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Specialties (comma separated)</Label>
                <Input value={newProvider.specialties} onChange={(e) => setNewProvider(p => ({ ...p, specialties: e.target.value }))}
                  placeholder="e.g. Tax Planning, Estate Law, Career Coaching" className="mt-1 rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Location</Label>
                  <Input value={newProvider.location} onChange={(e) => setNewProvider(p => ({ ...p, location: e.target.value }))}
                    placeholder="Atlanta, GA" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Pricing</Label>
                  <Input value={newProvider.pricing} onChange={(e) => setNewProvider(p => ({ ...p, pricing: e.target.value }))}
                    placeholder="$100/hr" className="mt-1 rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={newProvider.contact_email} onChange={(e) => setNewProvider(p => ({ ...p, contact_email: e.target.value }))}
                    placeholder="contact@example.com" className="mt-1 rounded-xl" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={newProvider.contact_phone} onChange={(e) => setNewProvider(p => ({ ...p, contact_phone: e.target.value }))}
                    placeholder="(555) 123-4567" className="mt-1 rounded-xl" />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <Input value={newProvider.website_url} onChange={(e) => setNewProvider(p => ({ ...p, website_url: e.target.value }))}
                  placeholder="https://yoursite.com" className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Calendly Link</Label>
                <Input value={newProvider.calendly_link} onChange={(e) => setNewProvider(p => ({ ...p, calendly_link: e.target.value }))}
                  placeholder="https://calendly.com/yourname" className="mt-1 rounded-xl" />
              </div>
              <Button onClick={handleRegister} disabled={registering} className="w-full h-12 bg-dark-slate text-white rounded-full font-semibold">
                {registering ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registering...</> : <><Plus className="w-4 h-4 mr-2" />Register as Provider</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ServiceDirectory;
